/**
 * A page's Markdown drawn as components (from `markdown.ts`'s data, never as HTML, so nothing
 * typed can run). Wiki links are buttons: `resolve` says whether a node has the name (those
 * that none has are marked `missing`), `onLink` follows one. A file shown in the page
 * (`![[path]]`) is its picture when `file` gives one, else a button to it (`onFile`).
 */
import { For, Match, Show, Switch, type JSX } from "solid-js";
import { parseMarkdown, type Inline } from "./markdown";

/** What the view needs to know about wiki links. */
export interface WikiProps {
  /** Whether a node has the name a link points at. */
  resolve: (target: string) => boolean;
  /** Follows a link. */
  onLink: (target: string) => void;
  /** What is known of one of the project's files: whether it is there, and a picture's address. */
  file?: (path: string) => { found: boolean; image: string | null };
  /** Opens one of the project's files. */
  onFile?: (path: string) => void;
}

/** The pieces of a line. */
function Pieces(props: { pieces: Inline[] } & WikiProps): JSX.Element {
  return (
    <For each={props.pieces}>
      {(p) => (
        <Switch>
          <Match when={p.type === "text" && p}>{(t) => t().text}</Match>
          <Match when={p.type === "strong" && p}>
            {(s) => (
              <strong>
                <Pieces {...props} pieces={s().children} />
              </strong>
            )}
          </Match>
          <Match when={p.type === "em" && p}>
            {(e) => (
              <em>
                <Pieces {...props} pieces={e().children} />
              </em>
            )}
          </Match>
          <Match when={p.type === "code" && p}>{(c) => <code>{c().text}</code>}</Match>
          <Match when={p.type === "embed" && p}>
            {(f) => {
              const known = () => props.file?.(f().target) ?? { found: false, image: null };
              return (
                <Show
                  when={known().image}
                  fallback={
                    <button type="button" class="wiki file" classList={{ missing: !known().found }} data-embed={f().target} title={known().found ? `Open ${f().target}` : `No file ${f().target} in the project`} onClick={() => props.onFile?.(f().target)}>
                      {f().label}
                    </button>
                  }
                >
                  {(src) => <img class="md-image" src={src()} alt={f().label} data-embed={f().target} onClick={() => props.onFile?.(f().target)} />}
                </Show>
              );
            }}
          </Match>
          <Match when={p.type === "wiki" && p}>
            {(w) => (
              <button
                type="button"
                class="wiki"
                classList={{ missing: !props.resolve(w().target) }}
                data-wiki={w().target}
                title={props.resolve(w().target) ? `Open ${w().target}` : `No page named ${w().target} yet: click to make it`}
                onClick={() => props.onLink(w().target)}
              >
                {w().label}
              </button>
            )}
          </Match>
        </Switch>
      )}
    </For>
  );
}

/** The page `text` drawn. */
export function MarkdownView(props: { text: string } & WikiProps): JSX.Element {
  const wiki = {
    get resolve() { return props.resolve; },
    get onLink() { return props.onLink; },
    get file() { return props.file; },
    get onFile() { return props.onFile; },
  };
  return (
    <div class="md">
      <For each={parseMarkdown(props.text)}>
        {(b) => (
          <Switch>
            <Match when={b.type === "heading" && b}>
              {(h) => (
                <Switch>
                  <Match when={h().level === 1}><h1><Pieces pieces={h().inline} {...wiki} /></h1></Match>
                  <Match when={h().level === 2}><h2><Pieces pieces={h().inline} {...wiki} /></h2></Match>
                  <Match when={h().level === 3}><h3><Pieces pieces={h().inline} {...wiki} /></h3></Match>
                </Switch>
              )}
            </Match>
            <Match when={b.type === "paragraph" && b}>{(p) => <p><Pieces pieces={p().inline} {...wiki} /></p>}</Match>
            <Match when={b.type === "quote" && b}>{(q) => <blockquote><Pieces pieces={q().inline} {...wiki} /></blockquote>}</Match>
            <Match when={b.type === "list" && b}>
              {(l) => {
                const items = () => <For each={l().items}>{(item) => <li><Pieces pieces={item} {...wiki} /></li>}</For>;
                return l().ordered ? <ol>{items()}</ol> : <ul>{items()}</ul>;
              }}
            </Match>
            <Match when={b.type === "rule"}><hr /></Match>
            <Match when={b.type === "code" && b}>{(c) => <pre><code>{c().text}</code></pre>}</Match>
          </Switch>
        )}
      </For>
    </div>
  );
}
